import React, { useState, useEffect, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useLocation, useSearchParams } from 'react-router-dom';
import { athleteAPI } from '../../services/api';
import { Athlete } from '../../types/mongodb';
import { getQuickDateRange } from './dateRangeUtils';
import { loadAnalyticsFilters, saveAnalyticsFilters, getPagePathFromRoute } from '../../utils/analyticsFilterStorage';
import {
  AnalyticsContainer,
  PageHeader,
  PageTitle,
  PageDescription,
  DataSelectionSection,
  SelectionGrid,
  SelectionGroup,
  GraphSection,
  GraphSectionWithPeriodSelector,
  GraphTitle,
  GraphContainer,
  EmptyState,
  PeriodSelectorTopRight,
  PeriodButtonWrapperSmall,
  PeriodButtonSmall,
  PeriodLabelSmall,
} from './styles';
import {
  DateRangeDropdown,
  AthletesDropdown,
} from './FilterDropdowns';
import { getAthleteColor } from '../../utils/athleteColors';


interface WeightDataPoint {
  date: string;
  [key: string]: string | number | null | undefined;
}

interface HeightDataPoint {
  date: string;
  [key: string]: string | number | null | undefined;
}

interface WeightAnalyticsProps {
  isJumperMode?: boolean;
}

const WeightAnalytics: React.FC<WeightAnalyticsProps> = ({ isJumperMode = false }) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const pagePath = getPagePathFromRoute(location.pathname, searchParams);
  
  // Load saved filters on mount
  const savedFilters = loadAnalyticsFilters(pagePath);
  
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [selectedAthletes, setSelectedAthletes] = useState<string[]>(savedFilters?.selectedAthletes || []);
  const [startDate, setStartDate] = useState<string>(savedFilters?.startDate || '');
  const [endDate, setEndDate] = useState<string>(savedFilters?.endDate || '');
  const [selectedPeriod, setSelectedPeriod] = useState<'daily' | 'weekly' | 'monthly'>(savedFilters?.selectedPeriod || 'daily');
  const isInitialMount = useRef(true);

  // Helper function to convert YYYY-MM-DD to DD/MM/YYYY for display
  const formatDateForDisplay = (dateString: string): string => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  };

  // Helper function to convert DD/MM/YYYY to YYYY-MM-DD for input value
  const formatDateForInput = (dateString: string): string => {
    if (!dateString) return '';
    // If already in YYYY-MM-DD format, return as is
    if (dateString.includes('-') && dateString.length === 10) {
      return dateString;
    }
    // If in DD/MM/YYYY format, convert to YYYY-MM-DD
    if (dateString.includes('/')) {
      const [day, month, year] = dateString.split('/');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return dateString;
  };
  const [weightDataDaily, setWeightDataDaily] = useState<WeightDataPoint[]>([]);
  const [weightDataWeekly, setWeightDataWeekly] = useState<WeightDataPoint[]>([]);
  const [weightDataMonthly, setWeightDataMonthly] = useState<WeightDataPoint[]>([]);
  const [heightDataDaily, setHeightDataDaily] = useState<HeightDataPoint[]>([]);
  const [heightDataWeekly, setHeightDataWeekly] = useState<HeightDataPoint[]>([]);
  const [heightDataMonthly, setHeightDataMonthly] = useState<HeightDataPoint[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [jumperAthleteId, setJumperAthleteId] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= 768);

  // Track window size for responsive margins
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const loadAthletes = async () => {
      try {
        setIsLoading(true);
        
        // If jumper mode, get the logged-in jumper's athlete ID first
        if (isJumperMode) {
          try {
            const myAthlete = await athleteAPI.getMyAthlete();
            const athleteId = myAthlete._id.toString();
            setJumperAthleteId(athleteId);
            setSelectedAthletes([athleteId]);
          } catch (error) {
            console.error('Error loading jumper athlete:', error);
          }
        }
        
        const data = await athleteAPI.getAll();
        setAthletes(data);
        
        // Don't set default dates - coach must select them
      } catch (error) {
        console.error('Error loading athletes:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadAthletes();
  }, [isJumperMode]);

  // Save filters whenever they change (skip on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    saveAnalyticsFilters(pagePath, {
      selectedAthletes,
      startDate,
      endDate,
      selectedPeriod,
    });
  }, [pagePath, selectedAthletes, startDate, endDate, selectedPeriod]);

  // Manual refresh function
  const handleUpdateDate = () => {
    if (selectedAthletes.length > 0 && startDate && endDate) {
      processWeightData();
      processHeightData();
    } else {
      setWeightDataDaily([]);
      setWeightDataWeekly([]);
      setWeightDataMonthly([]);
      setHeightDataDaily([]);
      setHeightDataWeekly([]);
      setHeightDataMonthly([]);
    }
  };

  const handleQuickDateRange = (range: string) => {
    const { startDate: newStartDate, endDate: newEndDate } = getQuickDateRange(range);
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  const handleAthletesSave = (selected: string[]) => {
    setSelectedAthletes(selected);
  };

  const processWeightData = () => {
    // Don't process if date range is not set or no athletes selected
    if (!startDate || !endDate || selectedAthletes.length === 0) {
      setWeightDataDaily([]);
      setWeightDataWeekly([]);
      setWeightDataMonthly([]);
      return;
    }
    
    // Create a map to store weight data by date
    const weightDataByDate: Map<string, Map<string, number>> = new Map();

    // Process date range - collect all weight entries
    selectedAthletes.forEach((athleteId) => {
      const athlete = athletes.find((a) => a._id === athleteId);
      if (!athlete || !athlete.weightHistory) return;

      const athleteName = `${athlete.firstName} ${athlete.lastName}`;
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);

      athlete.weightHistory.forEach((entry) => {
        const entryDate = new Date(entry.date);
        if (entryDate >= start && entryDate <= end) {
          if (!weightDataByDate.has(entry.date)) {
            weightDataByDate.set(entry.date, new Map());
          }
          weightDataByDate.get(entry.date)!.set(athleteName, entry.weight);
        }
      });
    });

    // Generate complete date range
    const start = new Date(startDate);
    const end = new Date(endDate);
    const weightDataPoints: WeightDataPoint[] = [];

    // Create entries for every day in the date range
    const currentDate = new Date(start);
    while (currentDate <= end) {
      const dateKey = currentDate.toISOString().split('T')[0];
      const dataPoint: WeightDataPoint = { date: dateKey };

      // Add weight data for each selected athlete
      selectedAthletes.forEach((athleteId) => {
        const athlete = athletes.find((a) => a._id === athleteId);
        if (athlete) {
          const athleteName = `${athlete.firstName} ${athlete.lastName}`;
          const weightForDate = weightDataByDate.get(dateKey)?.get(athleteName);
          dataPoint[athleteName] = weightForDate !== undefined ? weightForDate : null as any;
        }
      });

      weightDataPoints.push(dataPoint);
      currentDate.setDate(currentDate.getDate() + 1);
    }

    setWeightDataDaily(weightDataPoints);

    // Process weekly data
    const weightDataWeeklyPoints: WeightDataPoint[] = [];
    const weekStart = new Date(start);
    weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1); // Monday
    
    while (weekStart <= end) {
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      const weekKey = weekStart.toISOString().split('T')[0];
      const weekPoint: WeightDataPoint = { date: weekKey };
      
      selectedAthletes.forEach((athleteId) => {
        const athlete = athletes.find((a) => a._id === athleteId);
        if (!athlete) return;
        const athleteName = `${athlete.firstName} ${athlete.lastName}`;
        
        const weekWeights: number[] = [];
        const checkDate = new Date(weekStart);
        while (checkDate <= weekEnd && checkDate <= end) {
          const dateStr = checkDate.toISOString().split('T')[0];
          const weight = weightDataByDate.get(dateStr)?.get(athleteName);
          if (weight !== undefined) {
            weekWeights.push(weight);
          }
          checkDate.setDate(checkDate.getDate() + 1);
        }
        
        if (weekWeights.length > 0) {
          // Use the most recent weight of the week
          weekPoint[athleteName] = weekWeights[weekWeights.length - 1];
        } else {
          weekPoint[athleteName] = null as any;
        }
      });
      
      weightDataWeeklyPoints.push(weekPoint);
      weekStart.setDate(weekStart.getDate() + 7);
    }
    
    setWeightDataWeekly(weightDataWeeklyPoints);

    // Process monthly data
    const weightDataMonthlyPoints: WeightDataPoint[] = [];
    const monthStart = new Date(start.getFullYear(), start.getMonth(), 1);
    
    while (monthStart <= end) {
      const monthEnd = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);
      const monthLabel = monthStart.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      const monthPoint: WeightDataPoint = { date: monthLabel };
      
      selectedAthletes.forEach((athleteId) => {
        const athlete = athletes.find((a) => a._id === athleteId);
        if (!athlete) return;
        const athleteName = `${athlete.firstName} ${athlete.lastName}`;
        
        const monthWeights: number[] = [];
        const checkDate = new Date(monthStart);
        while (checkDate <= monthEnd && checkDate <= end) {
          const dateStr = checkDate.toISOString().split('T')[0];
          const weight = weightDataByDate.get(dateStr)?.get(athleteName);
          if (weight !== undefined) {
            monthWeights.push(weight);
          }
          checkDate.setDate(checkDate.getDate() + 1);
        }
        
        if (monthWeights.length > 0) {
          // Use the most recent weight of the month
          monthPoint[athleteName] = monthWeights[monthWeights.length - 1];
        } else {
          monthPoint[athleteName] = null as any;
        }
      });
      
      weightDataMonthlyPoints.push(monthPoint);
      monthStart.setMonth(monthStart.getMonth() + 1);
    }
    
    setWeightDataMonthly(weightDataMonthlyPoints);
  };

  const processHeightData = () => {
    // Don't process if date range is not set or no athletes selected
    if (!startDate || !endDate || selectedAthletes.length === 0) {
      setHeightDataDaily([]);
      setHeightDataWeekly([]);
      setHeightDataMonthly([]);
      return;
    }
    
    // Create a map to store height data by date
    const heightDataByDate: Map<string, Map<string, number>> = new Map();

    // Process date range - collect all height entries
    selectedAthletes.forEach((athleteId) => {
      const athlete = athletes.find((a) => a._id === athleteId);
      if (!athlete || !athlete.heightHistory) return;

      const athleteName = `${athlete.firstName} ${athlete.lastName}`;
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);

      athlete.heightHistory.forEach((entry) => {
        const entryDate = new Date(entry.date);
        if (entryDate >= start && entryDate <= end) {
          if (!heightDataByDate.has(entry.date)) {
            heightDataByDate.set(entry.date, new Map());
          }
          heightDataByDate.get(entry.date)!.set(athleteName, entry.height);
        }
      });
    });

    // Generate complete date range
    const start = new Date(startDate);
    const end = new Date(endDate);
    const heightDataPoints: HeightDataPoint[] = [];

    // Create entries for every day in the date range
    const currentDate = new Date(start);
    while (currentDate <= end) {
      const dateKey = currentDate.toISOString().split('T')[0];
      const dataPoint: HeightDataPoint = { date: dateKey };

      // Add height data for each selected athlete
      selectedAthletes.forEach((athleteId) => {
        const athlete = athletes.find((a) => a._id === athleteId);
        if (athlete) {
          const athleteName = `${athlete.firstName} ${athlete.lastName}`;
          const heightForDate = heightDataByDate.get(dateKey)?.get(athleteName);
          dataPoint[athleteName] = heightForDate !== undefined ? heightForDate : null as any;
        }
      });

      heightDataPoints.push(dataPoint);
      currentDate.setDate(currentDate.getDate() + 1);
    }

    setHeightDataDaily(heightDataPoints);

    // Process weekly data
    const heightDataWeeklyPoints: HeightDataPoint[] = [];
    const weekStart = new Date(start);
    weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1); // Monday
    
    while (weekStart <= end) {
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      const weekKey = weekStart.toISOString().split('T')[0];
      const weekPoint: HeightDataPoint = { date: weekKey };
      
      selectedAthletes.forEach((athleteId) => {
        const athlete = athletes.find((a) => a._id === athleteId);
        if (!athlete) return;
        const athleteName = `${athlete.firstName} ${athlete.lastName}`;
        
        const weekHeights: number[] = [];
        const checkDate = new Date(weekStart);
        while (checkDate <= weekEnd && checkDate <= end) {
          const dateStr = checkDate.toISOString().split('T')[0];
          const height = heightDataByDate.get(dateStr)?.get(athleteName);
          if (height !== undefined) {
            weekHeights.push(height);
          }
          checkDate.setDate(checkDate.getDate() + 1);
        }
        
        if (weekHeights.length > 0) {
          // Use the most recent height of the week
          weekPoint[athleteName] = weekHeights[weekHeights.length - 1];
        } else {
          weekPoint[athleteName] = null as any;
        }
      });
      
      heightDataWeeklyPoints.push(weekPoint);
      weekStart.setDate(weekStart.getDate() + 7);
    }
    
    setHeightDataWeekly(heightDataWeeklyPoints);

    // Process monthly data
    const heightDataMonthlyPoints: HeightDataPoint[] = [];
    const monthStart = new Date(start.getFullYear(), start.getMonth(), 1);
    
    while (monthStart <= end) {
      const monthEnd = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);
      const monthLabel = monthStart.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      const monthPoint: HeightDataPoint = { date: monthLabel };
      
      selectedAthletes.forEach((athleteId) => {
        const athlete = athletes.find((a) => a._id === athleteId);
        if (!athlete) return;
        const athleteName = `${athlete.firstName} ${athlete.lastName}`;
        
        const monthHeights: number[] = [];
        const checkDate = new Date(monthStart);
        while (checkDate <= monthEnd && checkDate <= end) {
          const dateStr = checkDate.toISOString().split('T')[0];
          const height = heightDataByDate.get(dateStr)?.get(athleteName);
          if (height !== undefined) {
            monthHeights.push(height);
          }
          checkDate.setDate(checkDate.getDate() + 1);
        }
        
        if (monthHeights.length > 0) {
          // Use the most recent height of the month
          monthPoint[athleteName] = monthHeights[monthHeights.length - 1];
        } else {
          monthPoint[athleteName] = null as any;
        }
      });
      
      heightDataMonthlyPoints.push(monthPoint);
      monthStart.setMonth(monthStart.getMonth() + 1);
    }
    
    setHeightDataMonthly(heightDataMonthlyPoints);
  };

  useEffect(() => {
    if (startDate && endDate && selectedAthletes.length > 0 && athletes.length > 0) {
      processWeightData();
      processHeightData();
    } else {
      setWeightDataDaily([]);
      setWeightDataWeekly([]);
      setWeightDataMonthly([]);
      setHeightDataDaily([]);
      setHeightDataWeekly([]);
      setHeightDataMonthly([]);
    }
  }, [selectedAthletes, startDate, endDate, athletes]);

  const handleAthleteToggle = (athleteId: string) => {
    setSelectedAthletes((prev) =>
      prev.includes(athleteId)
        ? prev.filter((id) => id !== athleteId)
        : [...prev, athleteId]
    );
  };


  // Helper function to calculate Y-axis domain for weight (min - 10, auto max)
  const calculateYAxisDomain = (data: WeightDataPoint[]): [number, string] => {
    let minValue = Infinity;
    
    data.forEach(point => {
      Object.keys(point).forEach(key => {
        if (key !== 'date') {
          const value = point[key];
          if (value !== null && value !== undefined && typeof value === 'number' && value < minValue) {
            minValue = value;
          }
        }
      });
    });
    
    if (minValue === Infinity) {
      return [0, 'auto'];
    }
    
    return [Math.max(0, minValue - 10), 'auto'];
  };

  // Helper function to calculate Y-axis domain for height (min - 5, auto max)
  const calculateHeightYAxisDomain = (data: HeightDataPoint[]): [number, string] => {
    let minValue = Infinity;
    
    data.forEach(point => {
      Object.keys(point).forEach(key => {
        if (key !== 'date') {
          const value = point[key];
          if (value !== null && value !== undefined && typeof value === 'number' && value < minValue) {
            minValue = value;
          }
        }
      });
    });
    
    if (minValue === Infinity) {
      return [0, 'auto'];
    }
    
    return [Math.max(0, minValue - 5), 'auto'];
  };

  const renderLines = () => {
    if (selectedAthletes.length === 0) return null;

    return selectedAthletes.map((athleteId, index) => {
      const athlete = athletes.find((a) => a._id === athleteId);
      if (!athlete) return null;

      const athleteName = `${athlete.firstName} ${athlete.lastName}`;
      const color = getAthleteColor(athlete, index);

      return (
        <Line
          key={athleteName}
          type="monotone"
          dataKey={athleteName}
          stroke={color}
          strokeWidth={2}
          dot={{ r: 4 }}
          activeDot={{ r: 6 }}
          connectNulls={true}
        />
      );
    });
  };

  if (isLoading) {
    return (
      <AnalyticsContainer>
        <EmptyState>Loading athletes...</EmptyState>
      </AnalyticsContainer>
    );
  }

  return (
    <AnalyticsContainer>
      <PageHeader>
        <PageTitle>Weight / Height Analytics</PageTitle>
        <PageDescription>
          Track and analyze athlete weight and height trends over time. Compare weight and height data across different date ranges to identify patterns and monitor progress.
        </PageDescription>
      </PageHeader>

      <DataSelectionSection>
        <SelectionGrid>
          {!isJumperMode && (
            <SelectionGroup>
              <AthletesDropdown
                athletes={athletes}
                selectedAthletes={selectedAthletes}
                onAthleteToggle={handleAthleteToggle}
                onSave={handleAthletesSave}
              />
            </SelectionGroup>
          )}

          <SelectionGroup>
            <DateRangeDropdown
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
              onQuickDateRange={handleQuickDateRange}
              onUpdate={handleUpdateDate}
              formatDateForInput={formatDateForInput}
            />
          </SelectionGroup>
        </SelectionGrid>
      </DataSelectionSection>

      <GraphSectionWithPeriodSelector>
        <GraphTitle>Weight Over Time</GraphTitle>
        {(() => {
          const hasDailyData = weightDataDaily.length > 0 && weightDataDaily.some(d => Object.keys(d).some(k => k !== 'date' && d[k] !== null));
          const hasWeeklyData = weightDataWeekly.length > 0 && weightDataWeekly.some(d => Object.keys(d).some(k => k !== 'date' && d[k] !== null));
          const hasMonthlyData = weightDataMonthly.length > 0 && weightDataMonthly.some(d => Object.keys(d).some(k => k !== 'date' && d[k] !== null));
          const hasAnyData = hasDailyData || hasWeeklyData || hasMonthlyData;
          
          return hasAnyData ? (
            <PeriodSelectorTopRight>
              <PeriodButtonWrapperSmall>
                <PeriodButtonSmall
                  $isSelected={selectedPeriod === 'daily'}
                  onClick={() => setSelectedPeriod('daily')}
                >
                  D
                </PeriodButtonSmall>
                <PeriodLabelSmall>Daily</PeriodLabelSmall>
              </PeriodButtonWrapperSmall>
              <PeriodButtonWrapperSmall>
                <PeriodButtonSmall
                  $isSelected={selectedPeriod === 'weekly'}
                  onClick={() => setSelectedPeriod('weekly')}
                >
                  W
                </PeriodButtonSmall>
                <PeriodLabelSmall>Weekly</PeriodLabelSmall>
              </PeriodButtonWrapperSmall>
              <PeriodButtonWrapperSmall>
                <PeriodButtonSmall
                  $isSelected={selectedPeriod === 'monthly'}
                  onClick={() => setSelectedPeriod('monthly')}
                >
                  M
                </PeriodButtonSmall>
                <PeriodLabelSmall>Monthly</PeriodLabelSmall>
              </PeriodButtonWrapperSmall>
            </PeriodSelectorTopRight>
          ) : null;
        })()}
        {startDate && endDate && selectedAthletes.length > 0 ? (
          (() => {
            const currentWeightData = selectedPeriod === 'daily' ? weightDataDaily : selectedPeriod === 'weekly' ? weightDataWeekly : weightDataMonthly;
            const hasData = currentWeightData.length > 0 && currentWeightData.some(d => Object.keys(d).some(k => k !== 'date' && d[k] !== null));
            
            return hasData ? (
              <GraphContainer>
                <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                  <LineChart 
                    data={currentWeightData} 
                    margin={{ 
                      top: 5, 
                      right: isMobile ? 10 : 30, 
                      left: isMobile ? 0 : 20, 
                      bottom: 5 
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis
                      dataKey="date"
                      stroke="#363636"
                      style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                      tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                      tickFormatter={(value) => {
                        if (selectedPeriod === 'monthly') return value;
                        if (selectedPeriod === 'weekly') {
                          try {
                            const date = new Date(value);
                            if (isNaN(date.getTime())) return value;
                            const weekEnd = new Date(date);
                            weekEnd.setDate(weekEnd.getDate() + 6);
                            return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                          } catch {
                            return value;
                          }
                        }
                        try {
                          const date = new Date(value);
                          if (isNaN(date.getTime())) return value;
                          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                        } catch {
                          return value;
                        }
                      }}
                    />
                    <YAxis
                      stroke="#363636"
                      style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                      tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                      label={{ value: 'Weight (kg)', angle: -90, position: 'insideLeft', style: { fontSize: '0.7rem', fontWeight: 900 } }}
                      domain={calculateYAxisDomain(currentWeightData)}
                        />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'var(--bg-primary)',
                        border: '1px solid var(--card-border)',
                        borderRadius: '8px',
                        fontFamily: 'Poppins',
                      }}
                      labelFormatter={(value) => {
                        try {
                          const date = new Date(value);
                          if (isNaN(date.getTime())) return value.toString();
                          return date.toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          });
                        } catch {
                          return value.toString();
                        }
                      }}
                      formatter={(tooltipValue) => {
                        if (tooltipValue === null || tooltipValue === undefined) {
                          return 'No data';
                        }
                        return `${tooltipValue} kg`;
                      }}
                    />
                    <Legend
                      wrapperStyle={{ fontFamily: 'Poppins', fontSize: isMobile ? '0.5rem' : '0.9rem' }}
                      iconSize={isMobile ? 8 : 12}
                    />
                    {renderLines()}
                  </LineChart>
                </ResponsiveContainer>
              </GraphContainer>
            ) : (
              <EmptyState>
                {selectedAthletes.length === 0
                  ? 'Please select at least one athlete to view weight data.'
                  : !startDate || !endDate
                  ? 'Please select a date range to view weight data.'
                  : 'No weight data available for the selected athletes and date range.'}
              </EmptyState>
            );
          })()
        ) : null}
      </GraphSectionWithPeriodSelector>

      <GraphSectionWithPeriodSelector>
        <GraphTitle>Height Over Time</GraphTitle>
        {(() => {
          const hasDailyData = heightDataDaily.length > 0 && heightDataDaily.some(d => Object.keys(d).some(k => k !== 'date' && d[k] !== null));
          const hasWeeklyData = heightDataWeekly.length > 0 && heightDataWeekly.some(d => Object.keys(d).some(k => k !== 'date' && d[k] !== null));
          const hasMonthlyData = heightDataMonthly.length > 0 && heightDataMonthly.some(d => Object.keys(d).some(k => k !== 'date' && d[k] !== null));
          const hasAnyData = hasDailyData || hasWeeklyData || hasMonthlyData;
          
          return hasAnyData ? (
            <PeriodSelectorTopRight>
              <PeriodButtonWrapperSmall>
                <PeriodButtonSmall
                  $isSelected={selectedPeriod === 'daily'}
                  onClick={() => setSelectedPeriod('daily')}
                >
                  D
                </PeriodButtonSmall>
                <PeriodLabelSmall>Daily</PeriodLabelSmall>
              </PeriodButtonWrapperSmall>
              <PeriodButtonWrapperSmall>
                <PeriodButtonSmall
                  $isSelected={selectedPeriod === 'weekly'}
                  onClick={() => setSelectedPeriod('weekly')}
                >
                  W
                </PeriodButtonSmall>
                <PeriodLabelSmall>Weekly</PeriodLabelSmall>
              </PeriodButtonWrapperSmall>
              <PeriodButtonWrapperSmall>
                <PeriodButtonSmall
                  $isSelected={selectedPeriod === 'monthly'}
                  onClick={() => setSelectedPeriod('monthly')}
                >
                  M
                </PeriodButtonSmall>
                <PeriodLabelSmall>Monthly</PeriodLabelSmall>
              </PeriodButtonWrapperSmall>
            </PeriodSelectorTopRight>
          ) : null;
        })()}
        {startDate && endDate && selectedAthletes.length > 0 ? (
          (() => {
            const currentHeightData = selectedPeriod === 'daily' ? heightDataDaily : selectedPeriod === 'weekly' ? heightDataWeekly : heightDataMonthly;
            const hasData = currentHeightData.length > 0 && currentHeightData.some(d => Object.keys(d).some(k => k !== 'date' && d[k] !== null));
            
            return hasData ? (
              <GraphContainer>
                <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                  <LineChart 
                    data={currentHeightData} 
                    margin={{ 
                      top: 5, 
                      right: isMobile ? 10 : 30, 
                      left: isMobile ? 0 : 20, 
                      bottom: 5 
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis
                      dataKey="date"
                      stroke="#363636"
                      style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                      tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                      tickFormatter={(value) => {
                        if (selectedPeriod === 'monthly') return value;
                        if (selectedPeriod === 'weekly') {
                          try {
                            const date = new Date(value);
                            if (isNaN(date.getTime())) return value;
                            const weekEnd = new Date(date);
                            weekEnd.setDate(weekEnd.getDate() + 6);
                            return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                          } catch {
                            return value;
                          }
                        }
                        try {
                          const date = new Date(value);
                          if (isNaN(date.getTime())) return value;
                          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                        } catch {
                          return value;
                        }
                      }}
                    />
                    <YAxis
                      stroke="#363636"
                      style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                      tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                      label={{ value: 'Height (cm)', angle: -90, position: 'insideLeft', style: { fontSize: '0.7rem', fontWeight: 900 } }}
                      domain={calculateHeightYAxisDomain(currentHeightData)}
                    />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--bg-primary)',
                    border: '1px solid var(--card-border)',
                    borderRadius: '8px',
                    fontFamily: 'Poppins',
                  }}
                  labelFormatter={(value) => {
                    try {
                      const date = new Date(value);
                      if (isNaN(date.getTime())) return value.toString();
                      return date.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      });
                    } catch {
                      return value.toString();
                    }
                  }}
                  formatter={(tooltipValue) => {
                    if (tooltipValue === null || tooltipValue === undefined) {
                      return 'No data';
                    }
                    return `${tooltipValue} cm`;
                  }}
                />
                <Legend
                  wrapperStyle={{ fontFamily: 'Poppins', fontSize: isMobile ? '0.5rem' : '0.9rem' }}
                  iconSize={isMobile ? 8 : 12}
                />
                {renderLines()}
              </LineChart>
            </ResponsiveContainer>
          </GraphContainer>
        ) : (
          <EmptyState>
            {selectedAthletes.length === 0
              ? 'Please select at least one athlete to view height data.'
              : !startDate || !endDate
              ? 'Please select a date range to view height data.'
              : 'No height data available for the selected athletes and date range.'}
              </EmptyState>
            );
          })()
        ) : null}
      </GraphSectionWithPeriodSelector>
    </AnalyticsContainer>
  );
};

export default WeightAnalytics;

